/** Pure rules for the publish dialog (PublishDialog.tsx). */
import { etDayShort, etTime } from '../labels'

export type When = 'now' | 'next_slot'

/** A slot at or before now is only a lag in the schedule runner: it goes within minutes. */
export function slotIsPast(nextSlot: string, now: Date): boolean {
  return new Date(nextSlot).getTime() <= now.getTime()
}

/** The slot option's title: "In the next open slot: Tue Oct 6 7:30 AM ET", or the past-slot wording. */
export function slotOptionText(nextSlot: string | null, releasesOn: boolean, now: Date): string {
  if (!releasesOn || !nextSlot) return 'In the next open slot'
  if (slotIsPast(nextSlot, now)) return 'In the next few minutes'
  return `In the next open slot: ${etDayShort(nextSlot)} ${etTime(nextSlot)}`
}

/** Why the slot option cannot be chosen, or null when it can. A scheduled or taken-down piece only goes "Now". */
export function slotDisabledReason(nextSlot: string | null, releasesOn: boolean, status?: string): string | null {
  if (status === 'queued') return 'This piece is already scheduled.'
  if (status === 'unpublished') return 'A piece that was taken down goes back up straight away.'
  if (!releasesOn) return "Releases are paused, so it can't be scheduled."
  if (!nextSlot) return 'No release slots are set.'
  return null
}

export function publishButtonLabel(when: When, slotIso: string | null, now: Date): string {
  if (when === 'now') return 'Publish now'
  if (!slotIso || slotIsPast(slotIso, now)) return 'Schedule (next few minutes)'
  return `Schedule for ${etDayShort(slotIso)}`
}

/** Open blocking items need the acknowledgement; a slot needs releases on and a slot to take. */
export function canSubmit(blockingCount: number, acknowledged: boolean, when: When, releasesOn: boolean, nextSlot: string | null, status?: string): boolean {
  if (blockingCount > 0 && !acknowledged) return false
  if (when === 'next_slot' && slotDisabledReason(nextSlot, releasesOn, status) !== null) return false
  return true
}

/** The line shown after a successful publish. */
export function publishedMessage(when: When, slotIso: string | null): string {
  if (when === 'now' || !slotIso) return when === 'now' ? 'Published.' : 'Scheduled.'
  return `Scheduled for ${etDayShort(slotIso)} ${etTime(slotIso)}.`
}
